import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.dismissed) {
      this.hide();
      return;
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
  }

  /**
   * Dismisses the announcement bar and keeps it dismissed across page loads.
   * @param {Event} event
   */
  dismiss(event) {
    event.preventDefault();

    if (this.dismissKey) {
      try {
        localStorage.setItem(this.dismissKey, 'dismissed');
      } catch (error) {
        // Storage can be unavailable in private browsing; still hide for this page.
      }
    }

    this.hide();
  }

  hide() {
    if (this.dismissClass) {
      document.documentElement.classList.add(this.dismissClass);
    }

    this.closest('.shopify-section')?.setAttribute('hidden', '');
    this.suspend();

    // The announcement bar is part of the header group. Its height is baked
    // into `--header-group-height`, which the transparent-header pages use to
    // pull the hero up under the header (see base.css / hero.liquid). Removing
    // the bar client-side doesn't reliably retrigger the header's own
    // recompute, leaving a blank strip where the bar was. Recompute it here,
    // mirroring the initial calculation in theme.liquid.
    this.#recomputeHeaderGroupHeight();
  }

  /**
   * Recomputes `--header-group-height` after the bar is dismissed so
   * transparent-header offsets no longer reserve the removed bar's height.
   */
  #recomputeHeaderGroupHeight() {
    const header = document.querySelector('header-component');
    const headerGroup = document.querySelector('#header-group');
    if (!header || !headerGroup) return;

    const headerHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) ||
      header.offsetHeight;

    let headerGroupHeight = 0;
    for (const element of headerGroup.children) {
      if (element === header || !(element instanceof HTMLElement)) continue;
      headerGroupHeight += element.offsetHeight;
    }

    // Transparent header overlays the first section, so its own height counts.
    if (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
      headerGroupHeight += headerHeight;
    }

    document.body.style.setProperty('--header-group-height', `${Math.round(headerGroupHeight)}px`);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get dismissKey() {
    return this.dataset.dismissKey;
  }

  get dismissClass() {
    return this.dataset.dismissClass;
  }

  get dismissed() {
    if (!this.dismissKey) return false;

    try {
      return localStorage.getItem(this.dismissKey) === 'dismissed';
    } catch (error) {
      return false;
    }
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
